/**
 * Long-text tab of the segment editor: the audio-synced transcription plus everything that only makes
 * sense while editing it (audio player, scenes, anchors).
 *
 * Extracted from `SegmentUI.tsx`. Two things are deliberately kept OUT of this component:
 *  - the cursor-timestamp state is owned by the parent, because the transcribe subsystem pushes a
 *    remote cursor position into it (`store.main.settings.transcribe.remoteCursorState`), and the
 *    parent is what stays mounted when the user switches tabs;
 *  - the `LT->ST` / `Cleanup` tool panels are rendered by the parent above the tab bar, so they do not
 *    flash in and out when switching fields.
 */
import {SleepAsync} from "js-vextensions";
import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {Button, Row, Text, TextArea, TextInput} from "react-vcomponents";
import {InfoButton} from "web-vcore";
import {UpdateJournalEntry} from "../../../../../Server/Commands/JournalEntryCommands.js";
import {GetEntities_WithUserTag, GetEntity} from "../../../../../Server/DB/entities.js";
import {EnsureSegmentUsingNewAnchorEntitiesField, FindLetterAnchorEntity, GetDreamAnchorEntities} from "../../../../../Server/DB/journalEntries/$journalEntry.js";
import {store} from "../../../../../Store/index.js";
import {Remote_NotifyCursorMove} from "../../../../../Utils/Bridge/Bridge_RemoteUserProcesses.js";
import {Clone_KeepTypeDef} from "../../../../../Utils/General/General.js";
import {AudioBlockPlayer, AudioBlockPlayer_Proxy} from "../../../../../Utils/ReactComponents/AudioBlockPlayer.js";
import {ParseLastTimestampBeforeCursor} from "../../../../../Utils/TimestampHandler.js";
import {SegmentScenesUI} from "../SegmentScenesUI.js";
import {SegmentTextTabProps, UpdateSegmentTextField} from "./@Shared.js";

export const SegmentTextTab_LongText = observer_mgl((props: SegmentTextTabProps & {
	effectiveWallClockTime: number|n,
	onCursorTimestampChange: (timestamp: number|null)=>any,
})=>{
	const {entry, segment, segmentIndex, effectiveWallClockTime, onCursorTimestampChange} = props;

	const transcribeState = store.main.settings.transcribe;
	const dreamAnchorEntityIds = GetDreamAnchorEntities(segment);
	const anchorEntities = GetEntities_WithUserTag("anchor");

	const HandleTextAreaPointerUp = async(el: HTMLTextAreaElement)=>{
		await SleepAsync(0); // wait for cursor position (selectionStart) to update after click
		const timestamp = ParseLastTimestampBeforeCursor(el.value, el.selectionStart, entry.sleepTime ?? null);
		onCursorTimestampChange(timestamp);
		if (segment.id) Remote_NotifyCursorMove(entry.id, segment.id, timestamp);
	};

	return (
		<>
			<Row>
				<TextArea style={{border: "1px solid black"}} autoSize={true} /*autoSize_minHeight={true}*/ value={segment.longText}
					onChange={val=>{
						UpdateSegmentTextField(props, "longText", val);
					}}
					onPointerUp={e=>HandleTextAreaPointerUp(e.target as HTMLTextAreaElement)}
					// on-pointer-up fails to trigger on touch-end over text-areas it seems (probably conflict with text-selection logic)
					onTouchEnd={e=>HandleTextAreaPointerUp(e.target as HTMLTextAreaElement)}/>
			</Row>
			<AudioBlockPlayer wallClockTime={effectiveWallClockTime}/>
			{transcribeState.cursorSendingActive && <AudioBlockPlayer_Proxy/>}
			<SegmentScenesUI entry={entry} segment={segment} hideIfNoScenes={false}/>
			<Row ml={5}>
				<Text>Anchors:</Text>
				<InfoButton ml={5} text={`
					The dream-world places ("anchors") this dream segment maps onto.
					Type a single letter to auto-fill that letter's anchor-entity.
				`.AsMultiline(0)}/>
				<Row style={{flexWrap: "wrap", gap: 5, marginLeft: 5}}>
					{dreamAnchorEntityIds.map((entityID, anchorIndex)=>{
						const anchorEntity = GetEntity(entityID);
						return (
							<Row key={anchorIndex}>
								<TextInput value={entityID} onChange={val_raw=>{
									let val = val_raw;

									// special case: if a single character is entered, auto-insert the id of the anchor-entity for that letter
									if (val_raw.trim().length == 1) {
										const newVal = FindLetterAnchorEntity(anchorEntities, val_raw.trim()[0].toLowerCase());
										if (newVal) val = newVal.id;
									}

									const segments_new = Clone_KeepTypeDef(entry.segments);
									EnsureSegmentUsingNewAnchorEntitiesField(segments_new[segmentIndex]);
									segments_new[segmentIndex].anchorEntities![anchorIndex] = val;
									new UpdateJournalEntry({id: entry.id, updates: {segments: segments_new}}).RunOnServer();
								}}/>
								{anchorEntity &&
								<Text ml={5} style={{flex: 1}}>{store.main.engine.journey.showAnchorEntities
									? (anchorEntity?.name ?? "")
									: (anchorEntity?.name?.slice(0, 1) ?? "")}</Text>}
								{!anchorEntity && <Button ml={5} mdIcon="dice-multiple" onClick={()=>{
									const segments_new = Clone_KeepTypeDef(entry.segments);
									EnsureSegmentUsingNewAnchorEntitiesField(segments_new[segmentIndex]);
									segments_new[segmentIndex].anchorEntities![anchorIndex] = anchorEntities.Random().id;
									new UpdateJournalEntry({id: entry.id, updates: {segments: segments_new}}).RunOnServer();
								}}/>}
							</Row>
						);
					})}
					<Button mdIcon="minus" onClick={()=>{
						const segments_new = Clone_KeepTypeDef(entry.segments);
						EnsureSegmentUsingNewAnchorEntitiesField(segments_new[segmentIndex]);
						segments_new[segmentIndex].anchorEntities!.pop();
						new UpdateJournalEntry({id: entry.id, updates: {segments: segments_new}}).RunOnServer();
					}}/>
					<Button mdIcon="plus" onClick={()=>{
						const segments_new = Clone_KeepTypeDef(entry.segments);
						EnsureSegmentUsingNewAnchorEntitiesField(segments_new[segmentIndex]);
						segments_new[segmentIndex].anchorEntities!.push("");
						new UpdateJournalEntry({id: entry.id, updates: {segments: segments_new}}).RunOnServer();
					}}/>
				</Row>
			</Row>
		</>
	);
});
